import React, { useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "../context/I18nContext";
import * as classesService from "../services/classes";
import * as lessonsService from "../services/lessons";
import { TimetableTab } from "./Admin";

export default function TeacherTimetable() {
  const navigate = useNavigate();
  const { effectiveRole, effectiveUser, accessToken } = useAuth();
  const { t, language } = useTranslation();

  const locale = useMemo(
    () => (language === "ar" ? "ar-EG-u-nu-latn" : language || "en"),
    [language],
  );
  const dir = useMemo(() => (language === "ar" ? "rtl" : "ltr"), [language]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [classGroups, setClassGroups] = useState([]);
  const [students, setStudents] = useState([]);

  useEffect(() => {
    if (effectiveRole !== "teacher") return;
    if (!accessToken || !effectiveUser) {
      setLoading(false);
      return;
    }

    let ignore = false;

    async function load() {
      setLoading(true);
      setError(null);

      try {
        const [lessonsPayload, classesPayload] = await Promise.all([
          lessonsService.fetchLessons({ accessToken }).catch(() => ({
            lessons: [],
          })),
          classesService.fetchClasses({ accessToken }).catch(() => ({
            classes: [],
          })),
        ]);

        if (ignore) return;

        const nextLessons = lessonsPayload.lessons || [];
        const nextClasses = classesPayload.classes || [];

        const seen = new Set();
        const uniqueStudents = [];

        nextClasses.forEach((group) => {
          (group?.students || []).forEach((student) => {
            if (student?.id && !seen.has(student.id)) {
              seen.add(student.id);
              uniqueStudents.push(student);
            }
          });
        });

        if (!uniqueStudents.length) {
          nextLessons.forEach((lesson) => {
            const student = lesson?.student;
            if (student?.id && !seen.has(student.id)) {
              seen.add(student.id);
              uniqueStudents.push(student);
            }
          });
        }

        setLessons(nextLessons);
        setClassGroups(nextClasses);
        setStudents(uniqueStudents);
      } catch (e) {
        if (!ignore) {
          setError(e?.message || t("errors.fetchFailed"));
        }
      } finally {
        if (!ignore) setLoading(false);
      }
    }

    load();
    return () => {
      ignore = true;
    };
  }, [accessToken, effectiveRole, effectiveUser, t]);

  if (effectiveRole !== "teacher") {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div style={{ direction: dir }}>
      {loading ? (
        <p style={{ opacity: 0.75 }}>{t("common.loading")}</p>
      ) : error ? (
        <div className="admin-error" role="alert">
          {error}
        </div>
      ) : (
        <TimetableTab
          t={t}
          dir={dir}
          locale={locale}
          users={effectiveUser ? [effectiveUser] : []}
          lessons={lessons}
          classGroups={classGroups}
          students={students}
          packs={[]}
          accessToken={accessToken}
          fixedTeacherId={effectiveUser?.id ? Number(effectiveUser.id) : null}
          onBack={() => navigate("/dashboard")}
          onLessonsChange={setLessons}
          onError={(message) => setError(message)}
        />
      )}
    </div>
  );
}
